import React from 'react';

import styled from 'styled-components';

import { showEventTime } from '@libs/EventDetails';
import { isBookingEvent, isJobEvent } from '@libs/EventTypes';
import { showPeriod } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import AuthorAndDepartment from '@components/CreatedBy/AuthorAndDepartment';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import NwDivider from '@components/Gui/NwDivider';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ProjectTitle from '@components/Project/ProjectTitle';

import EventAddress from './EventAddress';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EventToolTipContent = styled.div`
    min-width: 260px;
    max-width: 480px;

    .event-card-popover-body {
        padding: 1rem 1.5rem;

        .project-container {
            padding-bottom: 0.5rem;
            margin-bottom: 0.75rem;
            border-bottom: ${(props) => props.theme.DefaultBorder};
        }

        .ant-divider-horizontal.ant-divider-with-text {
            margin: 1rem 0 0.5rem 0;
        }

        h3.event-type {
            font-size: ${(props) => props.theme.FontSizeSm};
            margin: 0;
            line-height: 1em;
            font-weight: 300;
            text-transform: uppercase;
        }

        h1.event-title {
            line-height: 1.15em;
            margin: 0;
            text-transform: uppercase;
            font-weight: bold;
            font-size: ${(props) => props.theme.FontSizeBase};
        }

        .description {
            line-height: 1.1em;
            margin: 0.25rem 0;
            font-size: ${(props) => props.theme.FontSizeSm};
            font-weight: 300;
        }

        p {
            margin: 0.15rem 0;
            &.time {
                font-size: ${(props) => props.theme.FontSizeSm};
                font-weight: 400;

                span {
                    display: block;
                }
            }
        }

        ul {
            list-style-type: none;
            margin: 0;
            padding: 0;

            li {
                padding-left: 2rem;
                font-size: ${(props) => props.theme.FontSizeSm};
                &.cancel {
                    ${(props) => props.theme.LineThrough(1)}
                }
                &.confirmed {
                    font-weight: 600;
                }

                small {
                    margin-left: 1rem;
                    font-size: ${(props) => props.theme.FontSizeXSm};
                    text-transform: uppercase;
                }
            }
        }

        .event-address {
            font-size: ${(props) => props.theme.FontSizeSm};
            p {
                margin: 0;
            }
        }
    }

    .event-card-popover-footer {
        margin-top: 0.25rem;
        padding: 0.5rem 1.5rem 0.75rem 1.5rem;
        border-top: ${(props) => props.theme.DefaultBorder};

        p.event-author-and-department {
            svg {
                margin-right: 0.5rem;
            }

            font-size: ${(props) => props.theme.FontSizeSm};
            font-weight: 300;
            text-transform: uppercase;
            margin: 0;
        }
        small {
            text-transform: uppercase;
            display: block;
            font-size: ${(props) => props.theme.FontSizeXSm};
            padding-left: 1.3rem;
            line-height: 1.2em;
        }
    }
`;

const EventCardTooltip = React.memo(
    ({
        event,
        eventstyle,
        model,
        TypeName,
        FromDate,
        ToDate,
        title,
        Description,
        Project,
        ModelsDates,
        grouped,
        Address,
    }) => {
        const start = FromDate;
        const end = ToDate;
        const evtitle = title ? title : eventstyle.name;
        const modelDates = !isBookingEvent(TypeName) ? null : ModelsDates;

        const getModelsClassNames = (m) => {
            let myclasses = [];
            if (m.Cancel) {
                myclasses.push('cancel');
            }
            if (m.OptionNumber === 10) {
                myclasses.push('confirmed');
            }
            if (myclasses.length) {
                return myclasses.join(' ');
            }
            return '';
        };

        const ordinalOf = (i) => {
            const j = i % 10;
            const k = i % 100;
            if (j === 1 && k !== 11) {
                return i + 'st';
            }
            if (j === 2 && k !== 12) {
                return i + 'nd';
            }
            if (j === 3 && k !== 13) {
                return i + 'rd';
            }
            return i + 'th';
        };

        const GetModelsTexts = (m) => (
            <>
                {getContactName(m.ModelID)}
                {m.OptionNumber === 10 && isJobEvent(TypeName) && <small>confirmed</small>}
                {m.OptionNumber > 0 && m.OptionNumber < 10 && isJobEvent(TypeName) && (
                    <small className="option-number">{ordinalOf(m.OptionNumber)} option</small>
                )}
            </>
        );

        return (
            <EventToolTipContent>
                <div className="event-card-popover-body">
                    <h3 className="event-type">{eventstyle.name}</h3>
                    <h1 className="event-title">{evtitle}</h1>
                    {Description && <NwEllipsis text={Description} className="description" rows={3} />}
                    {Project && <ProjectTitle project={Project} popover />}
                    {model ? (
                        <></>
                    ) : (
                        <>
                            <NwDivider>Dates &amp; Models</NwDivider>
                            {isBookingEvent(TypeName) && (
                                <>
                                    {modelDates &&
                                        modelDates.map((md, index) => (
                                            <div key={index}>
                                                <p className="time">
                                                    {showEventTime(md.FromDate, md.ToDate, md.StartBreak, md.EndBreak)}
                                                    {md.CallTime && (
                                                        <span>
                                                            <strong>Call Time: {md.CallTime}</strong>
                                                        </span>
                                                    )}
                                                </p>
                                                <ul>
                                                    {md.Models.map((m, index) => (
                                                        <li className={getModelsClassNames(m)} key={index}>
                                                            {GetModelsTexts(m)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                </>
                            )}
                            {!isBookingEvent(TypeName) && TypeName !== 'Accommodation' && (
                                <div>
                                    <p className="time">{showEventTime(start, end)}</p>
                                    {grouped ? (
                                        <ul>
                                            {grouped.map((m, ind) => (
                                                <li key={ind}>{getContactName(m.ModelID)}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <ul>
                                            <li>{getContactName(event.ModelID)}</li>
                                        </ul>
                                    )}
                                </div>
                            )}
                            {TypeName === 'Accommodation' && (
                                <div>
                                    <p className="time">{showPeriod(event.FromDate, event.ToDate)}</p>
                                    <p>{getContactName(event.ModelID)}</p>
                                </div>
                            )}
                        </>
                    )}
                    {Address && (Address.Name || Address.Street || Address.City || Address.Country) && (
                        <>
                            <NwDivider>Address</NwDivider>
                            <EventAddress address={Address} inline />
                        </>
                    )}
                </div>
                <div className="event-card-popover-footer">
                    {event.Project ? (
                        <>
                            <p className="event-author-and-department">
                                <NwIcon icon={byPrefixAndName.fasl['circle-user']} />
                                <AuthorAndDepartment object={event.Project} />
                            </p>
                            {event.Project.UserCreated !== event.UserCreated && (
                                <small>
                                    <CreatedBy breaklines textonly noDiv object={event} extra="Event created" />
                                </small>
                            )}
                        </>
                    ) : (
                        <p className="event-author-and-department">
                            <NwIcon icon={byPrefixAndName.fasl['circle-user']} />
                            <AuthorAndDepartment object={event} />
                        </p>
                    )}
                </div>
            </EventToolTipContent>
        );
    },
    (prevProps, nextProps) => {
        // Memoization selettiva per tooltip
        return (
            prevProps.event.ID === nextProps.event.ID &&
            prevProps.event.title === nextProps.event.title &&
            prevProps.event.Description === nextProps.event.Description &&
            prevProps.event.FromDate === nextProps.event.FromDate &&
            prevProps.event.ToDate === nextProps.event.ToDate &&
            prevProps.model === nextProps.model &&
            prevProps.TypeName === nextProps.TypeName
        );
    }
);

export default EventCardTooltip;
